import React, { useState, useEffect, useRef } from "react";

const useCountAnimation = (
  end: number,
  duration: number = 2000,
  threshold: number = 0.3,
) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold },
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [isVisible, threshold]);

  useEffect(() => {
    if (!isVisible) return;
    let startTime: number;
    let animationFrame: number;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOutCubic * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isVisible, end, duration]);

  return { count, ref };
};

interface CounterComponentProps {
  endValue: number;
  suffix?: string;
  prefix?: string;
  title?: string;
  duration?: number;
  className?: string;
  numberClassName?: string;
  titleClassName?: string;
  containerClassName?: string;
  showThousandSeparator?: boolean;
  threshold?: number;
  startOnVisible?: boolean;
}
const CounterComponent: React.FC<CounterComponentProps> = ({
  endValue,
  suffix = "",
  prefix = "",
  title,
  duration = 2000,
  className = "",
  numberClassName = "",
  titleClassName = "",
  containerClassName = "",
  showThousandSeparator = true,
  threshold = 0.3,
  startOnVisible = true,
}) => {
  const { count, ref } = useCountAnimation(endValue, duration, threshold);

  const formatNumber = (num: number) => {
    if (showThousandSeparator) {
      return num.toLocaleString();
    }
    return num.toString();
  };

  return (
    <div
      ref={startOnVisible ? ref : null}
      className={`text-center ${containerClassName} ${className}`}
    >
      <div className={`font-medium ${numberClassName}`}>
        {prefix}
        {formatNumber(count)}
        {suffix}
      </div>
      {title && <div className={`${titleClassName}`}>{title}</div>}
    </div>
  );
};

export default CounterComponent;

export { useCountAnimation };
